import React, { useState, useEffect, useRef } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/free-solid-svg-icons';
import useHttpStatus from '~/commons/hooks/useHttpStatus';
import Article from '../blocks/Article.jsx';
import KeyLearning from '../blocks/KeyLearning.jsx';
import ExtraReading from '../blocks/ExtraReading.jsx';
import FormAnswer from '../forms/FormAnswer.jsx';

export const TryNowAnswer = ({
  subject,
  isSenior,
  show,
  onHide,
  providedArticle,
}) => {
  const [res, req] = useHttpStatus();
  const [answer, setAnswer] = useState(null);
  const scrollRef = useRef(null);
  const hasLoadedRef = useRef(false);
  const currentSubjectRef = useRef(null);
  const [startTime, setStartTime] = useState(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [frozenElapsed, setFrozenElapsed] = useState(null);
  // Move activeTab state to top level to avoid hooks order error
  const [activeTab, setActiveTab] = useState('article');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    // Reset if subject changed
    if (currentSubjectRef.current !== subject) {
      hasLoadedRef.current = false;
      currentSubjectRef.current = subject;
      setStartTime(null); // Reset timer when subject changes
    }

    if (!show || hasLoadedRef.current) return;

    // If article is provided, use it directly (direct mode)
    if (providedArticle) {
      hasLoadedRef.current = true;
      return;
    }

    // Otherwise, fetch random article (random mode)
    if (!subject) return;

    req({
      url: `/api/quiz/trainings/random-article?subject=${subject}&isSenior=${isSenior}`,
    });
    hasLoadedRef.current = true;
  }, [subject, isSenior, show, providedArticle]); // Added providedArticle to dependencies

  // Timer functionality
  useEffect(() => {
    if ((res.status === 'success' && res.payload) || providedArticle) {
      if (!startTime) {
        const now = Date.now();
        setStartTime(now);
        setCurrentTime(now);
      }
    }
  }, [res.status, res.payload, startTime, providedArticle]);

  useEffect(() => {
    if (!startTime) return undefined;

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [startTime]);

  // Back to top functionality
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef?.current) {
        const { scrollTop } = scrollRef.current;
        setShowBackToTop(scrollTop > 300); // Show button after scrolling 300px
      }
    };

    // Add a small delay to ensure the DOM element is attached
    const timer = setTimeout(() => {
      const scrollElement = scrollRef?.current;
      if (scrollElement) {
        scrollElement.addEventListener('scroll', handleScroll, { passive: true });
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      const scrollElement = scrollRef?.current;
      if (scrollElement) {
        scrollElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [show, res.payload, providedArticle]); // Depend on data being available

  const scrollToTop = () => {
    if (scrollRef?.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Calculate elapsed time
  const getElapsedTime = () => {
    if (frozenElapsed !== null) {
      const minutes = Math.floor(frozenElapsed / 60);
      const seconds = frozenElapsed % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds
        .toString()
        .padStart(2, '0')}`;
    }
    if (!startTime || currentTime < startTime) return '00:00';
    const elapsed = Math.floor((currentTime - startTime) / 1000);
    if (elapsed < 0) return '00:00';
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds
      .toString()
      .padStart(2, '0')}`;
  };

  if (!show) return null;

  const doc = providedArticle
    ? {
      _id: providedArticle._id || 'provided',
      article: providedArticle,
      answers: [{}],
      training: providedArticle.training || {
        _id: 'provided',
        title: providedArticle.title || '',
        subject: subject || 'unknown',
        isSenior: isSenior === 'true',
      },
    }
    : res.payload;
  // Handle loading state
  if (!providedArticle && res.status === 'pending') {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
        }}
      >
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">載入中...</span>
          </div>
          <div>載入練習題目中...</div>
        </div>
        <button className="btn btn-secondary mt-3" onClick={onHide}>
          取消
        </button>
      </div>
    );
  }

  // Handle error states
  if (
    !doc
    || !doc.article
    || (!providedArticle && (res.status === 'error' || !res.payload))
  ) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'white',
          zIndex: 999999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
        }}
      >
        {res.status === 'error' && res.code === 500
          ? '系統暫時沒有練習題目，請稍後再試'
          : '無法載入文章資料'}
        <button className="btn btn-primary mt-3" onClick={onHide}>
          關閉
        </button>
      </div>
    );
  }

  const { article } = doc;
  const { extraReading, keyLearning } = article || {};
  const { questions } = article || {};

  // Responsive breakpoints
  const isMobile = window.innerWidth <= 768;
  const isTablet = window.innerWidth <= 1024;
  const isDesktop = window.innerWidth > 1024;

  // Render different layouts based on device type
  if (isMobile) {
    return (
      <div
        className="try-now-answer-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#f5f1e8',
          zIndex: 999999,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Banner Header */}
        <div
          className="w-100"
          style={{
            backgroundColor: '#f5f1e8',
            padding: '0',
          }}
        >
          <img
            src="/assets/img/trainingBanner.png"
            alt="熱身專區"
            className="img-fluid w-100"
            style={{
              objectFit: 'cover',
              height: '100%',
            }}
          />
        </div>

        {/* React Bootstrap Tabs (sticky) */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1001,
            background: '#fff',
          }}
        >
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-0"
            justify
            style={{
              backgroundColor: '#fff',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Tab eventKey="article" title="文章內容">
              {/* Mobile Timer Display (sticky under tabs) */}
              <div
                style={{
                  padding: '15px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1000,
                }}
              >
                {answer
                  && answer.answer
                  && typeof answer.answer.score === 'number' && (
                    <>
                      <span
                        style={{
                          color: '#333',
                          fontWeight: 'bold',
                        }}
                      >
                        總分：
                      </span>
                      <span
                        style={{
                          backgroundColor: '#5ba3d4',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                        }}
                      >
                        {answer.answer.score}
                      </span>
                    </>
                )}
              </div>
            </Tab>
            <Tab eventKey="question" title="題目作答">
              {/* Mobile Timer Display (sticky under tabs) */}
              <div
                style={{
                  padding: '15px',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1000,
                }}
              >
                {answer
                  && answer.answer
                  && typeof answer.answer.score === 'number' && (
                    <>
                      <span
                        style={{
                          color: '#333',
                          fontWeight: 'bold',
                        }}
                      >
                        總分：
                      </span>
                      <span
                        style={{
                          backgroundColor: '#5ba3d4',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                        }}
                      >
                        {answer.answer.score}
                      </span>
                    </>
                )}
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, minHeight: 0 }}>
          {/* Article Tab Content */}
          {activeTab === 'article' && (
            <div id="tab-article">
              <div
                style={{
                  background: '#fff',
                  minHeight: '100%',
                  paddingBottom: '20px',
                }}
              >
                {/* Article Header with SVG icons */}
                <div
                  style={{
                    padding: '15px 10px',
                    borderBottom: '1px solid #e0e0e0',
                  }}
                >
                  {/* First row with subject icon and reading material */}
                  <div className="row align-items-center mb-3 g-1">
                    {/* Subject-specific SVG icon - leftmost */}
                    <div className="col-auto">
                      <img
                        src={`/assets/img/${subject}.svg`}
                        alt={subject}
                        style={{ width: '60px', height: '47px' }}
                      />
                    </div>
                    {/* Reading Material SVG - center */}
                    <div className="col text-center">
                      <div className="col-auto text-center article-material-title">
                        <img className="left" src="/assets/img/planet.png" />
                        閱讀材料
                        <img className="right" src="/assets/img/rocket.png" />
                      </div>
                    </div>
                    {/* Most right: Back button */}
                    <div
                      className="col-auto"
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        minWidth: '80px',
                      }}
                    >
                      <button
                        className="btn btn-link d-flex align-items-center"
                        style={{
                          color: '#401D00',
                          fontWeight: 'bold',
                          fontSize: '14px',
                          textDecoration: 'none',
                          padding: 0,
                          whiteSpace: 'nowrap',
                        }}
                        onClick={onHide}
                      >
                        <img
                          src="/assets/img/back.svg"
                          alt="返回"
                          style={{
                            width: '20px',
                            height: '20px',
                            marginRight: '4px',
                          }}
                        />
                        返回
                      </button>
                    </div>
                  </div>
                  {/* Second row with title centered under reading material */}
                  <div className="row">
                    <div className="col-auto" style={{ width: '60px' }} />
                    <div className="col text-center">
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#401D00',
                        }}
                      >
                        {article.title || ''}
                      </div>
                    </div>
                    <div className="col-auto" style={{ width: '60px' }} />
                  </div>
                </div>
                {/* Article Content */}
                <div
                  style={{
                    padding: '15px 10px',
                    lineHeight: 1.8,
                    fontSize: '14px',
                    color: '#333',
                    flex: 1,
                  }}
                >
                  <Article article={article} hideTags hideTitle />
                  {extraReading && extraReading.url && answer && (
                    <button
                      className="btn btn-info mt-3"
                      onClick={() => {
                        const s = scrollRef.current;
                        s.scrollTo(0, s.scrollHeight);
                      }}
                    >
                      查看延伸閱讀
                    </button>
                  )}
                </div>
                {/* Answer Results Section */}
                {answer && (
                  <div
                    style={{
                      padding: '15px 10px',
                      backgroundColor: '#f8f9fa',
                      borderTop: '1px solid #e0e0e0',
                    }}
                  >
                    <div className="readable">
                      {keyLearning && <KeyLearning>{keyLearning}</KeyLearning>}
                      {extraReading && extraReading.url && (
                        <ExtraReading
                          assignment={doc}
                          extraReading={extraReading}
                          answer={answer}
                        />
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {/* Question Tab Content */}
          {activeTab === 'question' && (
            <div id="tab-question">
              <div
                style={{
                  background: '#fff',
                  minHeight: '100%',
                  padding: '15px',
                }}
              >
                <FormAnswer
                  questions={questions}
                  answer={answer}
                  assignment={doc._id}
                  startTime={startTime}
                  currentTime={currentTime}
                  getElapsedTime={getElapsedTime}
                  onHide={onHide}
                  onSubmitted={(p) => {
                    setAnswer(p);
                    setFrozenElapsed(
                      Math.floor((Date.now() - startTime) / 1000),
                    );
                    setStartTime(null);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop/Tablet: Bootstrap grid layout (like PageReadingMission)
  return (
    <div
      className="try-now-answer-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: '#f5f1e8',
        zIndex: 999999,
        overflowY: 'auto',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Banner Header */}
      <div
        className="w-100"
        style={{
          backgroundColor: '#f5f1e8',
          padding: '0',
          flexShrink: 0,
        }}
      >
        <img
          src="/assets/img/trainingBanner.png"
          alt="熱身專區"
          className="img-fluid w-100"
          style={{
            objectFit: 'cover',
            height: '100%',
          }}
        />
      </div>

      {/* Main Content - Bootstrap Grid */}
      <div className="container-fluid" style={{ flex: 1, minHeight: 0 }}>
        <div className="row" style={{ height: '100%' }}>
          {/* Article Section */}
          <div
            className='col-12 col-md p-0 shadow-r'
            ref={scrollRef}
            style={{
              backgroundColor: '#ffffff',
              overflowY: 'auto',
              zIndex: 2,
            }}
          >
            <div style={{ padding: '20px' }}>
              {/* Article Header with SVG icons */}
              <div
                style={{
                  padding: '20px 0px',
                  borderBottom: '1px solid #e0e0e0',
                }}
              >
                {/* First row with subject icon and reading material */}
                <div className="row align-items-center mb-3 g-1">
                  {/* Subject-specific SVG icon - leftmost */}
                  <div className="col article-subjects">
                    <img src={`/assets/img/${subject}.svg`} alt={subject} />
                  </div>
                  <div className="col-auto text-center article-material-title">
                    <img className="left" src="/assets/img/planet.png" />
                    閱讀材料
                    <img className="right" src="/assets/img/rocket.png" />
                  </div>
                  {/* Most right: Back button */}
                  <div className="col article-btn-back d-flex justify-content-end">
                    <button
                      className="btn btn-link d-flex align-items-center"
                      onClick={onHide}
                    >
                      <img src="/assets/img/back.svg" alt="返回主頁" />
                      <span className="d-none d-sm-inline">返回主頁</span>
                      <span className="d-inline d-sm-none">返回</span>
                    </button>
                  </div>
                </div>
                {/* Second row with title centered under reading material */}
                <div className="row">
                  <div className="col text-center">
                    <div className="article-title">{article.title || ''}</div>
                  </div>
                </div>
              </div>

              {/* Article Content */}
              <div
                style={{
                  padding: '20px 0px',
                  lineHeight: 1.8,
                  fontSize: '16px',
                  color: '#333',
                  flex: 1,
                }}
              >
                <Article article={article} hideTags hideTitle />

                {extraReading && extraReading.url && answer && (
                  <button
                    className="btn btn-info mt-3"
                    onClick={() => {
                      const s = scrollRef.current;
                      s.scrollTo(0, s.scrollHeight);
                    }}
                  >
                    查看延伸閱讀
                  </button>
                )}
              </div>

              {/* Answer Results Section */}
              {answer && (
                <div
                  style={{
                    padding: '20px 0px',
                    backgroundColor: '#f8f9fa',
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <div className="readable">
                    {keyLearning && <KeyLearning>{keyLearning}</KeyLearning>}
                    {extraReading && extraReading.url && (
                      <ExtraReading
                        assignment={doc}
                        extraReading={extraReading}
                        answer={answer}
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Back to top button - floating */}
              {showBackToTop && (
                <button
                  className="btn btn-primary position-fixed d-flex align-items-center justify-content-center"
                  style={{
                    bottom: '20px',
                    right: '20px',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    zIndex: 1000000,
                    backgroundColor: '#FF831E',
                    border: 'none',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
                  }}
                  onClick={scrollToTop}
                  aria-label="回到頂部"
                >
                  <FontAwesomeIcon
                    icon={faArrowUp}
                    style={{
                      fontSize: '20px',
                      color: 'white',
                    }}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Questions Section */}
          <div
            className='col-12 col-lg-auto p-0 bg-paper'
            style={{
              width: '460px',
              backgroundColor: '#ffffff',
              boxShadow: isDesktop
                ? '-2px 0 8px rgba(0,0,0,0.1)'
                : '0 -2px 8px rgba(0,0,0,0.1)',
              maxHeight: isTablet ? '50vh' : 'auto',
              minHeight: isTablet ? '300px' : 'auto',
              display: 'flex',
              flexDirection: 'column',
              overflowY: isTablet ? 'auto' : 'visible',
            }}
          >
            {/* Form Answer Content */}
            <FormAnswer
              questions={questions}
              answer={answer}
              assignment={doc._id}
              startTime={startTime}
              currentTime={currentTime}
              getElapsedTime={getElapsedTime}
              onHide={onHide}
              onSubmitted={(p) => {
                setAnswer(p);
                // Freeze timer at submission
                setFrozenElapsed(Math.floor((Date.now() - startTime) / 1000));
                setStartTime(null);
                // Handle answer submission
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const PageTryNow = () => null;

export default PageTryNow;
